export interface SearchableComboboxOption {
    value: string;
    label: string;
}

function normalizeForMatch(text: string): string {
    return text.toLowerCase().trim();
}

function optionMatchesLabel(label: string, query: string): boolean {
    if (!query) {
        return true;
    }
    return normalizeForMatch(label).includes(normalizeForMatch(query));
}

function readOptionsFromTemplate(root: HTMLElement): SearchableComboboxOption[] {
    const template = root.querySelector<HTMLTemplateElement>('[data-searchable-combobox-template]');
    if (!template) {
        return [];
    }

    const options: SearchableComboboxOption[] = [];
    template.content.querySelectorAll<HTMLElement>('[data-searchable-combobox-source-option]').forEach((el) => {
        const value = el.dataset.value;
        if (value == null || value === '') {
            return;
        }
        options.push({
            value,
            label: el.textContent?.trim() ?? '',
        });
    });
    return options;
}

function initSearchableCombobox(root: HTMLElement): void {
    if (root.dataset.searchableComboboxInit === 'true') {
        return;
    }
    root.dataset.searchableComboboxInit = 'true';

    const valueInput = root.querySelector<HTMLInputElement>('[data-searchable-combobox-value]');
    const textInput = root.querySelector<HTMLInputElement>('[data-searchable-combobox-input]');
    const listbox = root.querySelector<HTMLUListElement>('[data-searchable-combobox-listbox]');
    if (!valueInput || !textInput || !listbox) {
        return;
    }

    const disabled = root.dataset.searchableComboboxDisabled === 'true' || textInput.disabled;
    const allOptions = readOptionsFromTemplate(root);

    let filteredOptions: SearchableComboboxOption[] = [...allOptions];
    let activeIndex = -1;
    let isOpen = false;

    const findLabelForValue = (value: string): string => {
        const match = allOptions.find((o) => o.value === value);
        return match?.label ?? '';
    };

    const syncDisplayFromValue = (): void => {
        const current = valueInput.value;
        if (current) {
            textInput.value = findLabelForValue(current);
        }
    };

    syncDisplayFromValue();

    const setExpanded = (open: boolean): void => {
        isOpen = open;
        textInput.setAttribute('aria-expanded', open ? 'true' : 'false');
        listbox.classList.toggle('hidden', !open);
        if (!open) {
            activeIndex = -1;
            textInput.removeAttribute('aria-activedescendant');
        }
    };

    const renderListbox = (): void => {
        listbox.innerHTML = '';

        if (filteredOptions.length === 0) {
            const empty = document.createElement('li');
            empty.className = 'searchable-combobox-empty';
            empty.setAttribute('role', 'presentation');
            empty.textContent = 'No matches';
            listbox.appendChild(empty);
            activeIndex = -1;
            textInput.removeAttribute('aria-activedescendant');
            return;
        }

        if (activeIndex >= filteredOptions.length) {
            activeIndex = filteredOptions.length - 1;
        }
        if (activeIndex < 0 && filteredOptions.length > 0) {
            activeIndex = 0;
        }

        filteredOptions.forEach((option, index) => {
            const li = document.createElement('li');
            const optionId = `${textInput.id}-option-${index}`;
            li.id = optionId;
            li.setAttribute('role', 'option');
            li.dataset.value = option.value;
            li.className = 'searchable-combobox-option';
            if (index === activeIndex) {
                li.classList.add('searchable-combobox-option--active');
                li.setAttribute('aria-selected', 'true');
                textInput.setAttribute('aria-activedescendant', optionId);
            } else {
                li.setAttribute('aria-selected', 'false');
            }
            li.textContent = option.label;
            listbox.appendChild(li);
        });
    };

    const applyFilter = (query: string): void => {
        filteredOptions = allOptions.filter((o) => optionMatchesLabel(o.label, query));
        activeIndex = filteredOptions.length > 0 ? 0 : -1;
        renderListbox();
    };

    const selectOption = (option: SearchableComboboxOption): void => {
        valueInput.value = option.value;
        textInput.value = option.label;
        setExpanded(false);
    };

    const selectActiveOption = (): boolean => {
        if (activeIndex < 0 || activeIndex >= filteredOptions.length) {
            return false;
        }
        selectOption(filteredOptions[activeIndex]);
        return true;
    };

    const moveActive = (delta: number): void => {
        if (filteredOptions.length === 0) {
            return;
        }
        if (activeIndex < 0) {
            activeIndex = delta > 0 ? 0 : filteredOptions.length - 1;
        } else {
            activeIndex = (activeIndex + delta + filteredOptions.length) % filteredOptions.length;
        }
        renderListbox();
        const activeEl = listbox.querySelector<HTMLElement>('.searchable-combobox-option--active');
        activeEl?.scrollIntoView({ block: 'nearest' });
    };

    textInput.addEventListener('click', () => {
        if (disabled) {
            return;
        }
        if (!isOpen) {
            applyFilter('');
            setExpanded(true);
        }
    });

    textInput.addEventListener('input', () => {
        if (disabled) {
            return;
        }
        valueInput.value = '';
        applyFilter(textInput.value.trim());
        setExpanded(true);
    });

    textInput.addEventListener('keydown', (event) => {
        if (disabled) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (!isOpen) {
                    applyFilter('');
                    setExpanded(true);
                    return;
                }
                moveActive(1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (!isOpen) {
                    applyFilter('');
                    setExpanded(true);
                    return;
                }
                moveActive(-1);
                break;
            case 'Enter':
                if (isOpen) {
                    event.preventDefault();
                    selectActiveOption();
                }
                break;
            case 'Escape':
                if (isOpen) {
                    event.preventDefault();
                    setExpanded(false);
                    syncDisplayFromValue();
                }
                break;
            case 'Tab':
                setExpanded(false);
                syncDisplayFromValue();
                break;
            default:
                break;
        }
    });

    listbox.addEventListener('mousedown', (event) => {
        event.preventDefault();
    });

    listbox.addEventListener('click', (event) => {
        const target = (event.target as HTMLElement).closest<HTMLElement>('.searchable-combobox-option');
        if (!target || target.dataset.value == null) {
            return;
        }
        const value = target.dataset.value;
        const option = filteredOptions.find((o) => o.value === value);
        if (option) {
            selectOption(option);
        }
    });

    document.addEventListener('click', (event) => {
        if (!root.contains(event.target as Node)) {
            if (isOpen) {
                setExpanded(false);
                syncDisplayFromValue();
            }
        }
    });
}

/**
 * Initializes all `[data-searchable-combobox]` widgets on the page.
 * Options are read from `<template data-searchable-combobox-template>` children
 * rendered via `searchable-combobox-option.jsp`.
 */
export function initSearchableComboboxes(): void {
    document.querySelectorAll<HTMLElement>('[data-searchable-combobox]').forEach(initSearchableCombobox);
}
